import { useEffect, useMemo, useState } from 'react';

import { useInstanceId } from '@frontend/hooks';
import { getOrRenewOptiIdToken } from '@frontend/oktaAuth';
import { socketService } from '@frontend/services/socketService';
import { sendSentryMessage } from '@frontend/utils/sendSentryError';

import type { SocketContextValue } from './socketContext';
import { SocketContext } from './socketContext';

type SocketProviderProps = {
  children: React.ReactNode;
};

export function SocketProvider({ children }: SocketProviderProps) {
  const [connected, setConnected] = useState(false);
  const { instanceId } = useInstanceId();

  useEffect(() => {
    let cancelled = false;
    let handleConnect: ((data?: unknown) => void) | null = null;
    let handleDisconnect: ((data?: unknown) => void) | null = null;
    let handleConnectError: ((err?: unknown) => void) | null = null;
    let handleReconnectFailed: ((...args: unknown[]) => void) | null = null;
    let handleReconnectAttempt: ((...args: unknown[]) => void) | null = null;

    const initializeSocket = async () => {
      if (!instanceId) {
        console.warn('Cannot initialize socket: no instance ID available');
        return;
      }

      try {
        const token = await getOrRenewOptiIdToken();

        // Check if effect was cleaned up while waiting for token
        if (cancelled) {
          return;
        }

        socketService.init(token, instanceId);

        // Define handlers as named functions so we can remove them specifically
        handleConnect = () => {
          console.warn('Socket connected to server');
          setConnected(true);
        };

        handleDisconnect = () => {
          console.warn('Socket disconnected from server');
          setConnected(false);
        };

        handleConnectError = (err?: unknown) => {
          console.error('Socket connection error:', err);
          setConnected(false);
        };

        // Manager events (reconnection lifecycle)
        handleReconnectFailed = () => {
          console.error('Socket reconnection failed after all attempts');
          sendSentryMessage(
            'Socket reconnection failed after maximum attempts',
            {
              instanceId,
              maxAttempts: 10,
            },
            'error'
          );
        };

        handleReconnectAttempt = (attemptNumber: unknown) => {
          console.warn(`Socket reconnection attempt ${attemptNumber}`);
        };

        // Register socket event handlers
        socketService.on('connect', handleConnect);
        socketService.on('disconnect', handleDisconnect);
        socketService.on('connect_error', handleConnectError);

        // Register Manager event handlers (for reconnection events)
        socketService.onManager('reconnect_failed', handleReconnectFailed);
        socketService.onManager('reconnect_attempt', handleReconnectAttempt);

        // Connect
        socketService.connect();
      } catch (error) {
        console.error('Failed to initialize socket with authentication:', error);
      }
    };

    initializeSocket();

    // Synchronous cleanup - runs immediately when effect unmounts
    return () => {
      cancelled = true;

      // Remove socket event handlers if they were registered
      if (handleConnect) socketService.off('connect', handleConnect);
      if (handleDisconnect) socketService.off('disconnect', handleDisconnect);
      if (handleConnectError) socketService.off('connect_error', handleConnectError);

      // Remove Manager event handlers if they were registered
      if (handleReconnectFailed)
        socketService.offManager('reconnect_failed', handleReconnectFailed);
      if (handleReconnectAttempt)
        socketService.offManager('reconnect_attempt', handleReconnectAttempt);

      // Only disconnect if socket was actually initialized
      if (socketService.getSocket()) {
        socketService.disconnect();
      }

      setConnected(false);
    };
  }, [instanceId]);

  const value = useMemo<SocketContextValue>(() => {
    const on = (event: string, callback: (data?: unknown) => void) => {
      socketService.on(event, callback);
    };

    const off = (event: string, callback?: (data?: unknown) => void) => {
      socketService.off(event, callback);
    };

    return {
      connected,
      on,
      off,
    };
  }, [connected]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}
